import React, { useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '@hooks/index';

// Components

import Form from '@lib/wrappers/Form';
import InstructionalText from '@components/ssap/notifications/InstructionalText';
import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '@components/ssap/notifications/ValidationErrorMessage';
import { ConditionalRendering } from '@components/common/conditional-rendering';

// ActionCreators / Selectors

import { changeData } from '@actions/ssap/ssapActionCreators';

import {
    getServerDate,
    getSsapData,
    selectFormData,
    selectHouseholdMembers,
    selectMedicaidEndDateOverride,
    useExchangeAndProgramNames,
} from '@reducers/appReducer';

// Utils

import { addToDateWithFormat, convertDateToUtc, subtractFromDateWithFormat } from '@utils/dates/date.utils';
import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { dataSyncingTools } from '@utils/tools/dataSyncingTools';
import { DATE_FORMAT2 } from '@utils/dates/dateMethods';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { parseFieldChangeArgs } from '@utils/ssap/parseFieldChangeArgs';

// Constants

import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';




const Page2_29_E5 = props => {

    const {
        formName,
        pageName
    } = props;

    const data = useAppSelector(getSsapData);
    const householdMembers = useAppSelector(selectHouseholdMembers);
    const medicaidEndDateOverride = useAppSelector(selectMedicaidEndDateOverride);
    const serverDate = useAppSelector(getServerDate);

    const futureThreshold = 60;
    const pastThreshold = medicaidEndDateOverride || 90;

    const { date1, date2 } = useMemo(
        () => {
            const date1 = subtractFromDateWithFormat(
                pastThreshold,
                'day',
                DATE_FORMAT2,
                convertDateToUtc(serverDate)
            );

            const date2 = addToDateWithFormat(
                futureThreshold,
                'day',
                DATE_FORMAT2,
                convertDateToUtc(serverDate)
            );

            return {
                date1,
                date2
            };
        },
        [ serverDate, medicaidEndDateOverride ]
    );

    const {
        taxHousehold: {
            thCommonParams: {
                hasSomeoneWithExpiringMedicaidChipDenial
            }
        }
    } = data;

    const {
        exchangeChip,
        exchangeMedicaid,
        exchangeMedicaidOrChip,
        exchangeMedicaidSlashChip
    } = useExchangeAndProgramNames();

    const {
        isFormSubmitted
    } = useAppSelector(state => selectFormData(state, formName));

    const dispatch = useAppDispatch();

    const onFieldChange_member = (...args) => {

        let { indexInArray, name, newData, value } = parseFieldChangeArgs(args);

        const newState = { ...data };

        switch(true) {
            case name === 'medicaidTermination' && !value:
                newData = {
                    ...newData,
                    ...cloneObject(EMPTY_OBJECTS.MEDICAID_TERMINATION_DETAILS),
                    medicaidTermination: false
                };
                break;
        }

        newState.taxHousehold.householdMember[indexInArray] = { ...newData };

        const syncedData = dataSyncingTools.syncThCommonProps(
            newState,
            { propName: 'termination' }
        );

        dispatch(changeData(syncedData));
    };

    const onFieldChange_none_of_above = (...args) => {

        const [ newData ] = args;

        const updateIndividualValues = hhm => {
            return {
                ...hhm,
                ...cloneObject(EMPTY_OBJECTS.MEDICAID_TERMINATION_DETAILS),
                medicaidTermination: false,
            };
        };

        const syncedData = dataSyncingTools.syncThCommonProps(
            newData,
            {
                customUpdater: updateIndividualValues,
                isCommon: true,
                propName: 'termination'
            }
        );

        dispatch(changeData(syncedData));
    };

    const replacements = { exchangeChip, exchangeMedicaid, exchangeMedicaidOrChip, exchangeMedicaidSlashChip };

    const getHouseholdMembers = householdMembers => {
        const combinedMembers = householdMembers.map((member, i) => {
            const {
                applicantGuid,
                applyingForCoverageIndicator,
                householdIncomeOrSizeChanged,
                name,
                medicaidChipExpirationDate,
                medicaidTermination,
            } = member;

            const personName = normalizeNameOfPerson(name);

            return (
                <ConditionalRendering key={applicantGuid} isTrue={applyingForCoverageIndicator}>
                    <li>
                        <UniversalInput
                            label = { {
                                text: 'common.input.label.non_internationalized_text',
                                labelTextReplacements: {
                                    text: personName
                                }
                            } }
                            inputData = { {
                                isInputRequired: false,
                                type: INPUT_TYPES.CHECKBOX,
                                name: 'medicaidTermination',
                                classes: [''],
                                checked: medicaidTermination,
                                value: medicaidTermination,
                                currentData: member,
                                fields: [ ],
                                isInputStandalone: true
                            } }
                            inputActions = { {
                                onFieldChange: onFieldChange_member
                            } }
                            indexInArray = { i }
                        />

                        <ConditionalRendering isTrue={medicaidTermination}>
                            <div className = 'grid-container' >
                                <UniversalInput
                                    label = { {
                                        labelClass: [ 'padding-0' ],
                                        text: 'ssap.input.label.last_day_of_coverage',
                                        labelTextReplacements: {
                                            ...replacements,
                                            personName
                                        }
                                    } }
                                    inputData = { {
                                        inputClass: [ '' ],
                                        isInputRequired: true,
                                        type: INPUT_TYPES.DATE_PICKER,
                                        name: 'medicaidChipExpirationDate',
                                        value: medicaidChipExpirationDate,
                                        valuesToMatch: [ date1, date2 ],
                                        validationType: VALIDATION_TYPES.DATE_BETWEEN_TWO_DATES,
                                        validationErrorMessage: VALIDATION_ERROR_MESSAGES.MEDICAID_TERMINATION,
                                        validationErrorValues: {futureThreshold, pastThreshold},
                                        currentData: member,
                                        fields: [],
                                        isInputStandalone: true
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange_member
                                    } }
                                    errorClasses = { [ 'margin-l-0' ] }
                                    indexInArray = { i }
                                />

                                <UniversalInput
                                    legend = { {
                                        legendText: 'ssap.input.legend.household_income_or_size_changed',
                                        legendTextReplacements: {
                                            personName
                                        },
                                        dataInstructions: 'instructional_text.income_or_household_size_changed',
                                    } }
                                    label = { {
                                        ulClass: [ 'margin-b-10' ],
                                        showRequiredIcon: true
                                    } }
                                    inputData = { {
                                        isInputRequired: true,
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'householdIncomeOrSizeChanged',
                                        value: householdIncomeOrSizeChanged,
                                        options: DROPDOWN_OPTIONS.YES_NO,
                                        validationType: VALIDATION_TYPES.CHOSEN,
                                        validationErrorMessage: VALIDATION_ERROR_MESSAGES.MAKE_SELECTION,
                                        currentData: member,
                                        fields: [ ],
                                        isInputStandalone: true
                                    } }
                                    inputActions = { {
                                        onFieldChange: onFieldChange_member
                                    } }
                                    indexInArray = { i }
                                    errorClasses = { [ 'margin-l-0' ] }
                                />
                            </div>
                        </ConditionalRendering>
                    </li>
                </ConditionalRendering>
            );
        });

        return (
            <ul className = 'usa-list usa-list--unstyled' role = 'group' aria-labelledby = 'medicaid_termination_details_legend_text'>
                {combinedMembers}

                <li key = 'hasSomeoneWithExpiringMedicaidChipDenial'>
                    <UniversalInput
                        label = { {
                            text: 'ssap.input.label.none_of_above',
                            isLabelRequired: false
                        } }
                        inputData = { {
                            isInputRequired: false,
                            type: INPUT_TYPES.CHECKBOX,
                            name: 'hasSomeoneWithExpiringMedicaidChipDenial',
                            checked: hasSomeoneWithExpiringMedicaidChipDenial === false,
                            value: hasSomeoneWithExpiringMedicaidChipDenial === false,
                            currentData: cloneObject(data),
                            fields: [ 'taxHousehold', 'thCommonParams' ],
                            reversedValue: true,
                            isInputStandalone: true
                        } }
                        inputActions={ {
                            onFieldChange: onFieldChange_none_of_above
                        } }
                    />
                </li>
            </ul>
        );
    };

    const topLevelValidity = useMemo(
        () => {
            return householdMembers
                .some(hhm => hhm.medicaidTermination) || hasSomeoneWithExpiringMedicaidChipDenial === false;
        },
        [ householdMembers, hasSomeoneWithExpiringMedicaidChipDenial ]
    );

    return (
        <Form
            { ...{
                formName,
                pageName,
                topLevelValidity
            } }
        >
            <div
                className = 'fade-in'
                id = 'fhMedicaidTerminationDetails'
            >
                <div className = 'subsection' >
                    <ValidationErrorMessage
                        isVisible = { isFormSubmitted && !topLevelValidity }
                        messageText = { VALIDATION_ERROR_MESSAGES.ANY_VALUE }
                        errorClasses = { [ 'margin-l-0' ] }
                    />
                    <fieldset>
                        <legend data-testid='medicaid_termination_details_legend'>
                            <InstructionalText
                                Component = {
                                    <p data-instructions = 'instructional_text.medicaid_termination' id = 'medicaid_termination_details_legend_text' >
                                        { 'ssap.family_household.medicaid_termination' }
                                    </p>
                                }
                                replacements={{ pastThreshold }}
                                childrenReplacements = { replacements }
                                required
                            />
                        </legend>
                        { getHouseholdMembers(householdMembers) }
                    </fieldset>
                </div>
            </div>
        </Form>
    );
};

export default Page2_29_E5;